import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Input } from "antd"
import { useTranslation } from "react-i18next"
import { useDebounce } from "hooks/useDebounce"
import { Icon } from "ui/Icon"
import styles from "./index.module.css"

const SEARCH_DEBOUNCE_MS = 400

/**
 * "Search a booking" in the calendar header. The term goes to the saved filters, which the
 * bookings request already forwards as `search` (see `buildBookingsParams`), so the calendar
 * filters server-side rather than in the browser.
 */
const CalendarSearch = ({ value = "", onChange }) => {
  const { t } = useTranslation()
  const [term, setTerm] = useState(value)
  const debouncedTerm = useDebounce(term, SEARCH_DEBOUNCE_MS)
  const lastSentTerm = useRef(value)

  // Keep up with a term restored from saved filters, without echoing it straight back.
  useEffect(() => {
    setTerm(value)
    lastSentTerm.current = value
  }, [value])

  useEffect(() => {
    if (debouncedTerm === lastSentTerm.current) return
    lastSentTerm.current = debouncedTerm
    onChange(debouncedTerm)
  }, [debouncedTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Input
      allowClear
      className={styles.search}
      value={term}
      onChange={(event) => setTerm(event.target.value)}
      placeholder={t("calendar.search_placeholder", { defaultValue: "Search a booking" })}
      prefix={<Icon name="Search" width={20} height={20} color="var(--color-text-secondary)" />}
    />
  )
}

CalendarSearch.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

export default CalendarSearch
